import { useState } from 'react';
import { format } from 'date-fns';
import type { RescheduleReason } from '@/domain/types';
import { RESCHEDULE_REASON_LABEL } from '@/utils/bookingSummary';

/**
 * Reschedule reason picker (Prompt G, 15 Jul ruling: a reason is mandatory).
 * Intercepts a calendar drag before it commits so a move can never happen
 * without a reason. Choosing "Other" requires a free-text note.
 */
const ORDER: RescheduleReason[] = [
  'client-move',
  'artist-move',
  'cancellation-fill',
  'gap-fill',
  'other',
];

interface Props {
  /** Optional: the client whose booking is moving (shown for context). */
  customerName?: string;
  fromIso: string;
  /** The target slot. Equal to `fromIso` on the "no new date yet" path. */
  toIso: string;
  onPick: (reason: RescheduleReason, note?: string) => void;
  onClose: () => void;
}

export default function RescheduleReasonChooser({
  customerName,
  fromIso,
  toIso,
  onPick,
  onClose,
}: Props) {
  const [otherOpen, setOtherOpen] = useState(false);
  const [note, setNote] = useState('');
  const moving = toIso !== fromIso;
  const fromLabel = format(new Date(fromIso), 'EEE d MMM · h:mmaaa');
  const toLabel = format(new Date(toIso), 'EEE d MMM · h:mmaaa');

  const pick = (reason: RescheduleReason) => {
    if (reason === 'other') {
      setOtherOpen(true);
      return;
    }
    onPick(reason);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-overlay/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        data-testid="reschedule-reason-chooser"
        role="dialog"
        aria-label="Choose a reschedule reason"
        className="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl border border-gold-dim/40 bg-ink-deep p-5 text-cream-soft"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gold-dim/50 sm:hidden" />
        <h2 className="heading-serif text-base text-cream">
          Why is {customerName ?? 'this booking'} moving?
        </h2>
        <p className="font-mono text-xs text-gold-bright mt-0.5">
          {moving ? `${fromLabel}  →  ${toLabel}` : `${fromLabel} · no new date yet`}
        </p>
        <p className="label-small-caps mt-4 mb-2">Reschedule reason (required)</p>
        <div className="grid gap-2">
          {ORDER.map((reason) => (
            <button
              key={reason}
              type="button"
              data-testid={`reschedule-reason-${reason}`}
              onClick={() => pick(reason)}
              className={clsxRow(reason === 'other' && otherOpen)}
            >
              <span className="block text-sm font-medium text-cream">
                {RESCHEDULE_REASON_LABEL[reason]}
              </span>
            </button>
          ))}
        </div>

        {otherOpen ? (
          <div className="mt-3">
            <label className="label-small-caps mb-1 block" htmlFor="reschedule-reason-note">
              Note (required for Other)
            </label>
            <textarea
              id="reschedule-reason-note"
              data-testid="reschedule-reason-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="e.g. client going overseas for a month"
              className="w-full rounded-md border border-gold-dim/40 bg-ink-black px-3 py-2 text-sm text-cream focus:border-gold-bright focus:outline-none"
            />
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                data-testid="reschedule-reason-save"
                disabled={note.trim().length === 0}
                onClick={() => onPick('other', note.trim())}
                className="btn-primary text-xs disabled:opacity-40"
              >
                Save reason
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function clsxRow(active: boolean): string {
  return [
    'flex items-center gap-3 rounded-lg border p-3 text-left transition',
    active ? 'border-gold-bright/70 bg-gold/10' : 'border-gold-dim/30 hover:border-gold-bright/70',
  ].join(' ');
}
